import { useCallback, useEffect, useState } from 'react'
import { AccountActivityModal } from './components/AccountActivityModal'
import { CreateUserModal } from './components/CreateUserModal'
import { EditProfileModal } from './components/EditProfileModal'
import { logPageVisit, login, logout, me, saveToken, type AuthUser } from './api'
import { useAccountsPortal } from './hooks/useAccountsPortal'
import { redirectTarget } from './lib/redirect'
import { AccountsScreen } from './screens/AccountsScreen'
import { ServicesScreen } from './screens/ServicesScreen'
import { errMsg } from './utils/error'

export default function App() {
	const [booting, setBooting] = useState(true)
	const [user, setUser] = useState<AuthUser | null>(null)
	const [screen, setScreen] = useState<'boardstats' | 'accounts'>('boardstats')
	const [error, setError] = useState<string | null>(null)

	const [loginValue, setLoginValue] = useState('')
	const [password, setPassword] = useState('')
	const [loading, setLoading] = useState(false)

	const reportError = useCallback((e: unknown) => {
		setError(errMsg(e))
	}, [])

	const dismissError = useCallback(() => {
		setError(null)
	}, [])

	const ap = useAccountsPortal(screen, user, reportError, dismissError)

	useEffect(() => {
		let active = true
		const run = async () => {
			try {
				const current = await me()
				if (active) {
					setUser(current)
					const target = redirectTarget()
					if (target === 'boardstats') {
						window.location.href = '/boardstats/'
						return
					}
					if (target === 'accounts') {
						setScreen('accounts')
					}
				}
			} catch {
				if (active) setUser(null)
			} finally {
				if (active) setBooting(false)
			}
		}
		void run()
		return () => {
			active = false
		}
	}, [])

	useEffect(() => {
		if (screen === 'boardstats' && user) {
			void logPageVisit('boardstats')
		}
	}, [screen, user])

	const submitLogin = useCallback(async () => {
		setLoading(true)
		setError(null)
		try {
			const data = await login(loginValue, password)
			saveToken(data.token)
			setUser(data.user)
			await logPageVisit('login')
			const target = redirectTarget()
			if (target === 'boardstats') {
				window.location.href = '/boardstats/'
				return
			}
			if (target === 'accounts') {
				setScreen('accounts')
			}
		} catch (e) {
			setError(errMsg(e))
		} finally {
			setLoading(false)
		}
	}, [loginValue, password])

	const isSuperAdmin = Boolean(user?.is_super_admin)

	if (booting) {
		return (
			<div className="page center-page">
				<div className="muted">Проверка сессии...</div>
			</div>
		)
	}

	if (!user) {
		return (
			<div className="page center-page">
				<div className="login-box">
					<h1>Вход</h1>
					<div className="field">
						<label htmlFor="login">Логин</label>
						<input id="login" value={loginValue} onChange={(e) => setLoginValue(e.target.value)} />
					</div>
					<div className="field">
						<label htmlFor="password">Пароль</label>
						<input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
					</div>
					{error && <div className="error">{error}</div>}
					<button className="primary-btn" onClick={() => void submitLogin()} disabled={loading}>
						{loading ? 'Вход...' : 'Войти'}
					</button>
				</div>
			</div>
		)
	}

	return (
		<div className="page">
			<header>
				<h1>Auth Portal</h1>
				<div className="header-right">
					<span>{user.login}</span>
					<button
						className="secondary-btn"
						onClick={async () => {
							try {
								await logout()
							} finally {
								saveToken(null)
								setUser(null)
							}
						}}
					>
						Выйти
					</button>
				</div>
			</header>

			<div className="tabs">
				<button className={screen === 'boardstats' ? 'active' : ''} onClick={() => setScreen('boardstats')}>
					Сервисы
				</button>
				{isSuperAdmin && (
					<button className={screen === 'accounts' ? 'active' : ''} onClick={() => setScreen('accounts')}>
						Учётные записи
					</button>
				)}
			</div>

			{error && <div className="error">{error}</div>}

			{screen === 'boardstats' ? (
				<ServicesScreen />
			) : (
				<AccountsScreen
					sortedRows={ap.sortedRows}
					events={ap.events}
					userSortableHeader={ap.userSortableHeader}
					onOpenRow={(row) => void ap.openUserActivity(row)}
					onCreateClick={() => ap.setCreateModalOpen(true)}
				/>
			)}

			{ap.activityUser && (
				<AccountActivityModal
					activityUser={ap.activityUser}
					currentUser={user}
					userEvents={ap.userEvents}
					userEventsLoading={ap.userEventsLoading}
					activeToggling={ap.activeToggling}
					onCloseBackdrop={ap.closeActivityModal}
					onToggleActive={ap.toggleAccountActive}
					onEditProfile={ap.beginEditProfile}
				/>
			)}

			{ap.editProfileModalUser && (
				<EditProfileModal
					user={ap.editProfileModalUser}
					editFullName={ap.editFullName}
					editPosition={ap.editPosition}
					passwordValue={ap.passwordValue}
					passwordConfirmValue={ap.passwordConfirmValue}
					positionOptions={ap.positionOptions}
					profileSaving={ap.profileSaving}
					profilePasswordInvalid={ap.profilePasswordInvalid}
					onEditFullName={ap.setEditFullName}
					onEditPosition={ap.setEditPosition}
					onPasswordValue={ap.setPasswordValue}
					onPasswordConfirmValue={ap.setPasswordConfirmValue}
					onBackdrop={ap.closeEditProfileModal}
					onCancel={ap.closeEditProfileModal}
					onSubmit={ap.submitProfileEdit}
				/>
			)}

			{ap.createModalOpen && (
				<CreateUserModal
					newLogin={ap.newLogin}
					newFullName={ap.newFullName}
					newPosition={ap.newPosition}
					newPassword={ap.newPassword}
					newBoardstats={ap.newBoardstats}
					newManage={ap.newManage}
					newSuper={ap.newSuper}
					positionOptions={ap.positionOptions}
					onNewLogin={ap.setNewLogin}
					onNewFullName={ap.setNewFullName}
					onNewPosition={ap.setNewPosition}
					onNewPassword={ap.setNewPassword}
					onNewBoardstats={ap.setNewBoardstats}
					onNewManage={ap.setNewManage}
					onNewSuper={ap.setNewSuper}
					onBackdrop={() => ap.setCreateModalOpen(false)}
					onCancel={() => ap.setCreateModalOpen(false)}
					onSubmit={ap.submitCreate}
				/>
			)}
		</div>
	)
}
